import { doc, setDoc, getDocs, getDoc, deleteDoc, collection, serverTimestamp, query, where, addDoc, updateDoc, writeBatch } from "firebase/firestore"
import moment from "moment"
import { db, getCollectionName } from "../firebase"
import { fetchFlightById } from "./queries"

export const syncBookingToGoogleSheet = async (bookingId) => {
    try {
        const bookingData = await fetchBookingById(bookingId)

        // Fetch outbound and inbound flight details if they are IDs
        let outboundFlightStr = bookingData.outboundFlight
        if (outboundFlightStr) {
            const outboundFlight = await fetchFlightById(outboundFlightStr)
            if (outboundFlight) {
                outboundFlightStr = `${outboundFlight.flightNo} (${outboundFlight.departureDate})`
            }
        }

        let inboundFlightStr = bookingData.inboundFlight
        if (inboundFlightStr) {
            const inboundFlight = await fetchFlightById(inboundFlightStr)
            if (inboundFlight) {
                inboundFlightStr = `${inboundFlight.flightNo} (${inboundFlight.departureDate})`
            }
        }

        const bookingDataToSend = {
            ...bookingData,
            outboundFlight: outboundFlightStr,
            inboundFlight: inboundFlightStr,
        }
        const response = await fetch('https://gs-handler-jhftjxdw7q-uc.a.run.app', {
            method: "POST",
            headers: {
                "Content-Type": "application/json",
            },
            body: JSON.stringify({ bookingData: bookingDataToSend }),
        })

        if (!response.ok) {
            const errorText = await response.text()
            return { success: false, error: errorText || response.statusText || "Failed to sync booking to Google Sheet" }
        }

        const data = await response.json()
        return { success: true, data }
    } catch (err) {
        return { success: false, error: err.message || "Unknown error" }
    }
}

export const addBookingToFirestore = async (bookingData, promoCode, status = "pending") => {
    try {
        if (promoCode) {
            const promoId = promoCode.trim().toUpperCase()
            const promoRef = doc(db, getCollectionName("promos"), promoId)

            const snap = await getDoc(promoRef)
            if (!snap.exists()) throw new Error("Promo code not found")

            const { isActive, redeemLimit, redeemed } = snap.data()
            if (!isActive) throw new Error("Promo code already used")

            await updateDoc(promoRef, {
                isActive: redeemLimit > (redeemed + 1),
                redeemed: redeemed + 1
            })
        }
        const bookingsCollection = collection(db, getCollectionName("bookings"))
        const bookingDocRef = doc(bookingsCollection)
        await setDoc(bookingDocRef, {
            ...bookingData,
            id: bookingDocRef.id,
            dateAdded: serverTimestamp(),
            status: status
        })
        try {
            await syncBookingToGoogleSheet(bookingDocRef.id)
        } catch (error) {
            console.error("Error syncing booking to Google Sheet:", error)
        }

        return bookingDocRef.id
    } catch (error) {
        console.error("Error adding booking: ", error)
        throw error
    }
}

export const updateBookingInFirestore = async (bookingId, updateData) => {
    const bookingRef = doc(db, getCollectionName("bookings"), bookingId)
    await updateDoc(bookingRef, updateData)
    try {
        await syncBookingToGoogleSheet(bookingId)
    } catch (error) {
        console.error("Error syncing booking to Google Sheet:", error)
    }
}

export const fetchBookingById = async (bookingId) => {
    try {
        const bookingRef = doc(db, getCollectionName("bookings"), bookingId)
        const bookingSnap = await getDoc(bookingRef)
        if (!bookingSnap.exists()) {
            throw new Error("Booking not found")
        }
        return { id: bookingSnap.id, ...bookingSnap.data() }
    } catch (error) {
        console.error("Error fetching booking by ID:", error)
        throw error
    }
}

export const fetchPromo = async (promoCode) => {
    try {
        const promoQuery = query(
            collection(db, getCollectionName("promos")),
            where("code", "==", promoCode.trim())
        )

        const querySnapshot = await getDocs(promoQuery)
        if (querySnapshot.empty) {
            return null
        }

        const promo = querySnapshot.docs[0].data() // Get the first matching document        

        return promo
    } catch (error) {
        console.error("Error fetching promo code:", error)
        return null
    }
}

// Function to add new inventory item
export const addInventoryItem = async (inventoryData) => {
    try {
        const inventoryCollection = collection(db, getCollectionName("inventory"))
        // create the document reference to get the ID
        const docRef = doc(inventoryCollection)
        // set the data including the ID
        await setDoc(docRef, {
            ...inventoryData,
            id: docRef.id
        })
        return docRef.id
    } catch (error) {
        console.error("Error adding inventory item:", error)
        throw error
    }
}


//Function to bulk upload Inventory items
export const addBulkInventoryItems = async (inventoryItems) => {
    try {
        const inventoryCollection = collection(db, getCollectionName("inventory"))
        const batch = writeBatch(db)

        inventoryItems.forEach(item => {
            const docRef = doc(inventoryCollection)
            batch.set(docRef, {
                ...item,
                id: docRef.id
            })
        })

        await batch.commit()
        return inventoryItems.length
    } catch (error) {
        console.error("Error adding bulk inventory items:", error)
        throw error
    }
}

export const addBulkPromoItems = async (promoDataset) => {
    try {
        const promos = collection(db, getCollectionName("promos"))
        const batch = writeBatch(db)

        promoDataset.forEach(item => {
            const codeId = item.code.trim().toUpperCase()
            const docRef = doc(promos, codeId)

            batch.set(docRef, {
                ...item,
                code: codeId,
                redeemLimit: 1,
                redeemed: 0,
                isActive: true,
                createdAt: serverTimestamp()
            })
        })

        await batch.commit()
        return promoDataset.length
    } catch (error) {
        console.error("Error adding bulk promos:", error)
        throw error
    }
}

export const fetchFutureFlights = async () => {
    try {
        const today = moment().startOf('day').format('YYYY-MM-DD')
        const q = query(
            collection(db, getCollectionName("inventory")),
            where("departureDate", ">=", today)
        )
        const querySnapshot = await getDocs(q)
        return querySnapshot.docs.map(doc => ({
            id: doc.id,
            ...doc.data()
        }))
    } catch (error) {
        console.error("Error fetching future flights:", error)
        throw new Error('Failed to load existing flights')
    }
}

// Function to initialize inventory data (if needed)
export const initializeInventory = async () => {
    try {
        const inventoryCollection = collection(db, getCollectionName("inventory"))

        // Your existing inventory data
        const inventoryData = [
            {
                // id: 1,
                // departure: "TLV",
                // arrival: "JFK",
                departureTime: "23:30",
                arrivalTime: "05:50 (+1)",
                departureDate: "2025-02-08",
                flightNo: "IZ991",
                availableTickets: 5,
                price: "599.00"
            },
            {
                // id: 2,
                // departure: "JFK",
                // arrival: "TLV",
                departureTime: "15:00",
                arrivalTime: "09:00 (+1)",
                departureDate: "2025-02-09",
                flightNo: "IZ992",
                availableTickets: 5,
                price: "599.00"
            },
            {
                // id: 3,
                // departure: "TLV",
                // arrival: "JFK",
                departureTime: "12:00",
                arrivalTime: "18:20",
                departureDate: "2025-02-10",
                flightNo: "IZ991",
                availableTickets: 5,
                price: "599.00"
            },
            {
                // id: 4,
                // departure: "JFK",
                // arrival: "TLV",
                departureTime: "15:00",
                arrivalTime: "09:00 (+1)",
                departureDate: "2025-02-11",
                flightNo: "IZ992",
                availableTickets: 5,
                price: "599.00"
            },
            {
                // id: 5,
                // departure: "TLV",
                // arrival: "JFK",
                departureTime: "12:00",
                arrivalTime: "18:20",
                departureDate: "2025-02-12",
                flightNo: "IZ991",
                availableTickets: 5,
                price: "599.00"
            },
            {
                // id: 6,
                // departure: "JFK",
                // arrival: "TLV",
                departureTime: "15:55",
                arrivalTime: "10:00 (+1)",
                departureDate: "2025-02-13",
                flightNo: "IZ992",
                availableTickets: 5,
                price: "599.00"
            },
            {
                // id: 7,
                // departure: "TLV",
                // arrival: "JFK",
                departureTime: "23:30",
                arrivalTime: "05:50 (+1)",
                departureDate: "2025-02-15",
                flightNo: "IZ991",
                availableTickets: 5,
                price: "599.00"
            },
            {
                // id: 8,
                // departure: "JFK",
                // arrival: "TLV",
                departureTime: "15:00",
                arrivalTime: "09:00 (+1)",
                departureDate: "2025-02-16",
                flightNo: "IZ992",
                availableTickets: 5,
                price: "599.00"
            },
            {
                // id: 9,
                // departure: "TLV",
                // arrival: "JFK",
                departureTime: "12:00",
                arrivalTime: "18:20",
                departureDate: "2025-02-17",
                flightNo: "IZ991",
                availableTickets: 5,
                price: "599.00"
            },
            {
                // id: 10,
                // departure: "JFK",
                // arrival: "TLV",
                departureTime: "15:00",
                arrivalTime: "09:00 (+1)",
                departureDate: "2025-02-18",
                flightNo: "IZ992",
                availableTickets: 5,
                price: "599.00"
            },
            {
                // id: 11,
                // departure: "TLV",
                // arrival: "JFK",
                departureTime: "12:00",
                arrivalTime: "18:20",
                departureDate: "2025-02-19",
                flightNo: "IZ991",
                availableTickets: 5,
                price: "599.00"
            },
            {
                // id: 12,
                // departure: "JFK",
                // arrival: "TLV",
                departureTime: "15:55",
                arrivalTime: "10:00 (+1)",
                departureDate: "2025-02-20",
                flightNo: "IZ992",
                availableTickets: 5,
                price: "599.00"
            },
            {
                // id: 13,
                // departure: "TLV",
                // arrival: "JFK",
                departureTime: "23:30",
                arrivalTime: "05:50 (+1)",
                departureDate: "2025-02-22",
                flightNo: "IZ991",
                availableTickets: 5,
                price: "599.00"
            },
            {
                // id: 14,
                // departure: "JFK",
                // arrival: "TLV",
                departureTime: "15:00",
                arrivalTime: "09:00 (+1)",
                departureDate: "2025-02-23",
                flightNo: "IZ992",
                availableTickets: 5,
                price: "599.00"
            },
            {
                // id: 15,
                // departure: "TLV",
                // arrival: "JFK",
                departureTime: "12:00",
                arrivalTime: "18:20",
                departureDate: "2025-02-24",
                flightNo: "IZ991",
                availableTickets: 5,
                price: "599.00"
            },
            {
                // id: 16,
                // departure: "JFK",
                // arrival: "TLV",
                departureTime: "15:00",
                arrivalTime: "09:00 (+1)",
                departureDate: "2025-02-25",
                flightNo: "IZ992",
                availableTickets: 5,
                price: "599.00"
            },
            {
                // id: 17,
                // departure: "TLV",
                // arrival: "JFK",
                departureTime: "12:00",
                arrivalTime: "18:20",
                departureDate: "2025-02-26",
                flightNo: "IZ991",
                availableTickets: 5,
                price: "599.00"
            },
            {
                // id: 18,
                // departure: "JFK",
                // arrival: "TLV",
                departureTime: "15:55",
                arrivalTime: "10:00 (+1)",
                departureDate: "2025-02-27",
                flightNo: "IZ992",
                availableTickets: 5,
                price: "599.00"
            },
            {
                // id: 19,
                // departure: "TLV",
                // arrival: "JFK",
                departureTime: "23:30",
                arrivalTime: "05:50 (+1)",
                departureDate: "2025-03-01",
                flightNo: "IZ991",
                availableTickets: 5,
                price: "599.00"
            },
            {
                // id: 20,
                // departure: "JFK",
                // arrival: "TLV",
                departureTime: "15:00",
                arrivalTime: "09:00 (+1)",
                departureDate: "2025-03-02",
                flightNo: "IZ992",
                availableTickets: 5,
                price: "599.00"
            },
            {
                // id: 21,
                // departure: "TLV",
                // arrival: "JFK",
                departureTime: "12:00",
                arrivalTime: "18:20",
                departureDate: "2025-03-03",
                flightNo: "IZ991",
                availableTickets: 5,
                price: "599.00"
            },
            {
                // id: 22,
                // departure: "JFK",
                // arrival: "TLV",
                departureTime: "15:00",
                arrivalTime: "09:00 (+1)",
                departureDate: "2025-03-04",
                flightNo: "IZ992",
                availableTickets: 5,
                price: "599.00"
            },
            {
                // id: 23,
                // departure: "TLV",
                // arrival: "JFK",
                departureTime: "12:00",
                arrivalTime: "18:20",
                departureDate: "2025-03-05",
                flightNo: "IZ991",
                availableTickets: 5,
                price: "599.00"
            },
            {
                // id: 24,
                // departure: "JFK",
                // arrival: "TLV",
                departureTime: "15:55",
                arrivalTime: "10:00 (+1)",
                departureDate: "2025-03-06",
                flightNo: "IZ992",
                availableTickets: 5,
                price: "599.00"
            },
            {
                // id: 25,
                // departure: "TLV",
                // arrival: "JFK",
                departureTime: "23:30",
                arrivalTime: "06:50 (+1)",
                departureDate: "2025-03-08",
                flightNo: "IZ991",
                availableTickets: 5,
                price: "599.00"
            },
            {
                // id: 26,
                // departure: "JFK",
                // arrival: "TLV",
                departureTime: "15:00",
                arrivalTime: "08:00 (+1)",
                departureDate: "2025-03-09",
                flightNo: "IZ992",
                availableTickets: 5,
                price: "599.00"
            },
            {
                // id: 27,
                // departure: "TLV",
                // arrival: "JFK",
                departureTime: "12:00",
                arrivalTime: "19:20",
                departureDate: "2025-03-10",
                flightNo: "IZ991",
                availableTickets: 5,
                price: "599.00"
            },
            {
                // id: 28,
                // departure: "JFK",
                // arrival: "TLV",
                departureTime: "15:00",
                arrivalTime: "08:00 (+1)",
                departureDate: "2025-03-11",
                flightNo: "IZ992",
                availableTickets: 5,
                price: "599.00"
            },
            {
                // id: 29,
                // departure: "TLV",
                // arrival: "JFK",
                departureTime: "12:00",
                arrivalTime: "19:20",
                departureDate: "2025-03-12",
                flightNo: "IZ991",
                availableTickets: 5,
                price: "599.00"
            },
            {
                // id: 30,
                // departure: "JFK",
                // arrival: "TLV",
                departureTime: "16:00",
                arrivalTime: "09:00 (+1)",
                departureDate: "2025-03-13",
                flightNo: "IZ992",
                availableTickets: 5,
                price: "599.00"
            },
            {
                // id: 31,
                // departure: "TLV",
                // arrival: "JFK",
                departureTime: "23:30",
                arrivalTime: "06:50 (+1)",
                departureDate: "2025-03-15",
                flightNo: "IZ991",
                availableTickets: 5,
                price: "599.00"
            },
            {
                // id: 32,
                // departure: "JFK",
                // arrival: "TLV",
                departureTime: "15:00",
                arrivalTime: "08:00 (+1)",
                departureDate: "2025-03-16",
                flightNo: "IZ992",
                availableTickets: 5,
                price: "599.00"
            },
            {
                // id: 33,
                // departure: "TLV",
                // arrival: "JFK",
                departureTime: "12:00",
                arrivalTime: "19:20",
                departureDate: "2025-03-17",
                flightNo: "IZ991",
                availableTickets: 5,
                price: "599.00"
            },
            {
                // id: 34,
                // departure: "JFK",
                // arrival: "TLV",
                departureTime: "15:00",
                arrivalTime: "08:00 (+1)",
                departureDate: "2025-03-18",
                flightNo: "IZ992",
                availableTickets: 5,
                price: "599.00"
            },
            {
                // id: 35,
                // departure: "TLV",
                // arrival: "JFK",
                departureTime: "12:00",
                arrivalTime: "19:20",
                departureDate: "2025-03-19",
                flightNo: "IZ991",
                availableTickets: 5,
                price: "599.00"
            },
            {
                // id: 36,
                // departure: "JFK",
                // arrival: "TLV",
                departureTime: "16:00",
                arrivalTime: "09:00 (+1)",
                departureDate: "2025-03-20",
                flightNo: "IZ992",
                availableTickets: 5,
                price: "599.00"
            },
            {
                // id: 37,
                // departure: "TLV",
                // arrival: "JFK",
                departureTime: "23:30",
                arrivalTime: "06:50 (+1)",
                departureDate: "2025-03-22",
                flightNo: "IZ991",
                availableTickets: 5,
                price: "599.00"
            },
            {
                // id: 38,
                // departure: "JFK",
                // arrival: "TLV",
                departureTime: "15:00",
                arrivalTime: "08:00 (+1)",
                departureDate: "2025-03-23",
                flightNo: "IZ992",
                availableTickets: 5,
                price: "599.00"
            },
            {
                // id: 39,
                // departure: "TLV",
                // arrival: "JFK",
                departureTime: "12:00",
                arrivalTime: "19:20",
                departureDate: "2025-03-24",
                flightNo: "IZ991",
                availableTickets: 5,
                price: "599.00"
            },
            {
                // id: 40,
                // departure: "JFK",
                // arrival: "TLV",
                departureTime: "15:00",
                arrivalTime: "08:00 (+1)",
                departureDate: "2025-03-25",
                flightNo: "IZ992",
                availableTickets: 5,
                price: "599.00"
            },
            {
                // id: 41,
                // departure: "TLV",
                // arrival: "JFK",
                departureTime: "12:00",
                arrivalTime: "19:20",
                departureDate: "2025-03-26",
                flightNo: "IZ991",
                availableTickets: 5,
                price: "599.00"
            },
            {
                // id: 42,
                // departure: "JFK",
                // arrival: "TLV",
                departureTime: "16:00",
                arrivalTime: "09:00 (+1)",
                departureDate: "2025-03-27",
                flightNo: "IZ992",
                availableTickets: 5,
                price: "599.00"
            },
            {
                // id: 43,
                // departure: "TLV",
                // arrival: "JFK",
                departureTime: "23:30",
                arrivalTime: "06:50 (+1)",
                departureDate: "2025-03-29",
                flightNo: "IZ991",
                availableTickets: 5,
                price: "599.00"
            },
            {
                // id: 44,
                // departure: "JFK",
                // arrival: "TLV",
                departureTime: "15:00",
                arrivalTime: "08:00 (+1)",
                departureDate: "2025-03-30",
                flightNo: "IZ992",
                availableTickets: 5,
                price: "599.00"
            },
            {
                // id: 45,
                // departure: "TLV",
                // arrival: "JFK",
                departureTime: "12:00",
                arrivalTime: "19:20",
                departureDate: "2025-03-31",
                flightNo: "IZ991",
                availableTickets: 5,
                price: "599.00"
            }
        ]

        // Add each inventory item to Firebase
        for (const item of inventoryData) {
            await addDoc(inventoryCollection, item)
        }

        console.log("Inventory initialized successfully")
    } catch (error) {
        console.error("Error initializing inventory:", error)
        throw error
    }
}

// Function to update inventory item
export const updateInventoryItem = async (id, updateData) => {
    try {
        const { id: _, ...dataToSave } = updateData
        const docRef = doc(db, getCollectionName("inventory"), id)
        await setDoc(docRef, dataToSave, { merge: true })
    } catch (error) {
        console.error("Error updating inventory item:", error)
        throw error
    }
}

export const deleteInventoryItem = async (id) => {
    try {
        const docRef = doc(db, getCollectionName("inventory"), id)
        await deleteDoc(docRef)
    } catch (error) {
        console.error("Error deleting inventory item:", error)
        throw error
    }
}

export const addFlight = async (flightData) => {
    try {
        const flightsCollection = collection(db, getCollectionName("flights"))
        await setDoc(doc(flightsCollection, flightData.flightNo), flightData)
        return flightData.flightNo
    } catch (error) {
        console.error("Error adding flight:", error)
        throw error
    }
}

export const updateFlight = async (id, updateData) => {
    try {
        const { id: _, ...dataToSave } = updateData
        const docRef = doc(db, getCollectionName("flights"), id)
        await setDoc(docRef, dataToSave, { merge: true })
    } catch (error) {
        console.error("Error updating flight:", error)
        throw error
    }
}

export const addAirport = async (airportData) => {
    try {
        const airportsCollection = collection(db, getCollectionName("airports"))
        await setDoc(doc(airportsCollection, airportData.code), airportData)
        return airportData.code
    } catch (error) {
        console.error("Error adding airport:", error)
        throw error
    }
}

export const updateAirport = async (id, updateData) => {
    try {
        const { id: _, ...dataToSave } = updateData
        const docRef = doc(db, getCollectionName("airports"), id)
        await setDoc(docRef, dataToSave, { merge: true })
    } catch (error) {
        console.error("Error updating airport:", error)
        throw error
    }
}

export const deleteAirport = async (id) => {
    try {
        const docRef = doc(db, getCollectionName("airports"), id)
        await deleteDoc(docRef)
    } catch (error) {
        console.error("Error deleting airport:", error)
        throw error
    }
}

export const createPromo = async (promoData) => {
    try {
        const promoCollection = collection(db, getCollectionName("promos"))
        await setDoc(doc(promoCollection, promoData.code), {
            ...promoData,
            isActive: true,
            createdAt: serverTimestamp(),
        })
        return promoData.code
    } catch (error) {
        console.error("Error adding promo:", error)
        throw error
    }
}

export const updatePromo = async (code, updateData) => {
    try {
        const { code: _, ...dataToSave } = updateData
        const docRef = doc(db, getCollectionName("promos"), code)
        await setDoc(docRef, dataToSave, { merge: true })
    } catch (error) {
        console.error("Error updating promo:", error)
        throw error
    }
}

export const deletePromo = async (code) => {
    try {
        const docRef = doc(db, getCollectionName("promos"), code)
        await deleteDoc(docRef)
    } catch (error) {
        console.error("Error deleting promo:", error)
        throw error
    }
}